import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Page, Layout, Card, BlockStack, TextField, Text, Button, Banner,
    InlineStack, Thumbnail, Divider,
} from '@shopify/polaris'
import { PlusIcon, XIcon } from '@shopify/polaris-icons'
import { createDraftOrder, type DraftOrderCreateInput } from '../lib/graphql'
import { formatCurrency } from '../lib/currency'
import { CustomerPicker } from '../components/CustomerPicker'
import { DiscountModal, type DiscountData } from '../components/DiscountModal'
import { ShippingModal, type ShippingData } from '../components/ShippingModal'
import { TaxPopover } from '../components/TaxPopover'

type LineItem = {
    variantId: string
    productTitle: string
    variantTitle: string
    price: string
    quantity: number
    imageUrl?: string
}

// TODO: fetch actual store currency from shop settings instead of hardcoding
const STORE_CURRENCY = 'EUR'

export default function CreateDraftOrder() {
    const navigate = useNavigate()

    const [lineItems, setLineItems] = useState<LineItem[]>([])
    const [customerSelection, setCustomerSelection] = useState<any>(null)
    const [addressOverrides, setAddressOverrides] = useState<any>(null)
    const [creating, setCreating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [discountOpen, setDiscountOpen] = useState(false)
    const [shippingOpen, setShippingOpen] = useState(false)
    const [discount, setDiscount] = useState<DiscountData>({
        code: '', applyAutomatic: false, customDiscount: false,
        discountType: 'amount', discountValue: '0.00', reason: '',
    })
    const [shipping, setShipping] = useState<ShippingData>({
        type: 'rate', rateName: 'regular', ratePrice: '2.00',
        customName: 'Free shipping', customPrice: '0.00',
    })
    const [chargeTaxes, setChargeTaxes] = useState(true)

    // Product picker via App Bridge ResourcePicker
    const openProductPicker = useCallback(async () => {
        const selected = await window.shopify?.resourcePicker?.({
            type: 'product',
            multiple: true,
            action: 'add',
        })
        if (!selected) return

        setLineItems(prev => {
            const updated = [...prev]
            for (const product of selected) {
                for (const variant of (product.variants || [])) {
                    const idx = updated.findIndex(li => li.variantId === variant.id)
                    if (idx >= 0) {
                        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 }
                    } else {
                        updated.push({
                            variantId: variant.id,
                            productTitle: product.title,
                            variantTitle: variant.title === 'Default Title' ? '' : variant.title,
                            price: variant.price,
                            quantity: 1,
                            imageUrl: product.images?.[0]?.originalSrc || product.featuredImage?.url,
                        })
                    }
                }
            }
            return updated
        })
    }, [])

    const updateQuantity = useCallback((variantId: string, quantity: number) => {
        if (quantity < 1) return
        setLineItems(prev => prev.map(li =>
            li.variantId === variantId ? { ...li, quantity } : li
        ))
    }, [])

    const removeLineItem = useCallback((variantId: string) => {
        setLineItems(prev => prev.filter(li => li.variantId !== variantId))
    }, [])

    const handleCustomerRemove = useCallback(() => {
        setCustomerSelection(null)
    }, [])

    const itemCount = lineItems.reduce((sum, li) => sum + li.quantity, 0)
    const subtotal = lineItems.reduce((sum, li) => sum + parseFloat(li.price) * li.quantity, 0)
    const discountAmount = discount.customDiscount
        ? (discount.discountType === 'percentage'
            ? subtotal * parseFloat(discount.discountValue || '0') / 100
            : parseFloat(discount.discountValue || '0'))
        : 0
    const shippingAmount = shipping.type === 'rate'
        ? parseFloat(shipping.ratePrice || '0')
        : parseFloat(shipping.customPrice || '0')
    const hasShippableItems = lineItems.some(li => !li.productTitle.toLowerCase().includes('gift card'))
    const total = subtotal - discountAmount + shippingAmount
    const canCreate = lineItems.length > 0 && !creating

    // Create — build full DraftOrderInput from all form state
    const handleCreate = useCallback(async () => {
        if (lineItems.length === 0) return
        setCreating(true)
        setError(null)
        try {
            const customer = customerSelection?.customer
            const location = customerSelection?.location
            const isB2B = customerSelection?.isB2B

            // Build addresses from B2B company location or customer default
            const shippingAddr = isB2B && location?.shippingAddress
                ? {
                    firstName: location.shippingAddress.firstName || customer?.firstName || '',
                    lastName: location.shippingAddress.lastName || customer?.lastName || '',
                    company: location.shippingAddress.companyName || '',
                    address1: location.shippingAddress.address1 || '',
                    address2: location.shippingAddress.address2 || '',
                    city: location.shippingAddress.city || '',
                    zip: location.shippingAddress.zip || '',
                    countryCode: location.shippingAddress.countryCode || '',
                    provinceCode: location.shippingAddress.province || '',
                    phone: location.shippingAddress.phone || customer?.phone || '',
                }
                : customer?.defaultAddress
                    ? {
                        firstName: customer.defaultAddress.firstName || customer.firstName || '',
                        lastName: customer.defaultAddress.lastName || customer.lastName || '',
                        address1: customer.defaultAddress.address1 || '',
                        address2: customer.defaultAddress.address2 || '',
                        city: customer.defaultAddress.city || '',
                        zip: customer.defaultAddress.zip || '',
                        countryCode: customer.defaultAddress.countryCode || '',
                        provinceCode: customer.defaultAddress.province || '',
                        phone: customer.defaultAddress.phone || customer.phone || '',
                    }
                    : null

            const billingAddr = isB2B && location?.billingAddress
                ? {
                    firstName: location.billingAddress.firstName || customer?.firstName || '',
                    lastName: location.billingAddress.lastName || customer?.lastName || '',
                    company: location.billingAddress.companyName || '',
                    address1: location.billingAddress.address1 || '',
                    address2: location.billingAddress.address2 || '',
                    city: location.billingAddress.city || '',
                    zip: location.billingAddress.zip || '',
                    countryCode: location.billingAddress.countryCode || '',
                    provinceCode: location.billingAddress.province || '',
                    phone: location.billingAddress.phone || customer?.phone || '',
                }
                : shippingAddr

            // Apply modal overrides if user edited addresses
            const finalShipping = addressOverrides?.shipping || shippingAddr
            const finalBilling = addressOverrides?.billing || billingAddr
            const finalEmail = addressOverrides?.contact?.email || customer?.email || undefined
            const finalPhone = addressOverrides?.contact?.phone || customer?.phone || undefined


            // Build input
            const input: DraftOrderCreateInput = {
                lineItems: lineItems.map(li => ({ variantId: li.variantId, quantity: li.quantity })),
                customerId: customer?.id || null,
                email: finalEmail,
                phone: finalPhone,
                shippingAddress: finalShipping,
                billingAddress: finalBilling,
                useCustomerDefaultAddress: !isB2B && !!customer && !finalShipping,
                taxExempt: !chargeTaxes,
            }

            // B2B purchasing entity — replaces customerId
            if (isB2B && location) {
                const companyProfile = customer?.companyContactProfiles?.[0]
                if (companyProfile) {
                    delete input.customerId
                    input.purchasingEntity = {
                        purchasingCompany: {
                            companyId: companyProfile.company.id,
                            companyLocationId: location.id,
                            companyContactId: companyProfile.id,
                        },
                    }
                }
            }

            // Discount
            if (discount.code) {
                input.discountCodes = [discount.code]
            }
            if (discount.applyAutomatic) {
                input.acceptAutomaticDiscounts = true
            }
            if (discount.customDiscount && parseFloat(discount.discountValue || '0') > 0) {
                input.appliedDiscount = {
                    title: discount.reason || 'Custom discount',
                    description: discount.reason || 'Custom discount',
                    value: parseFloat(discount.discountValue),
                    valueType: discount.discountType === 'percentage' ? 'PERCENTAGE' : 'FIXED_AMOUNT',
                }
            }

            // Shipping
            if (hasShippableItems) {
                if (shipping.type === 'rate' && shipping.ratePrice) {
                    input.shippingLine = { title: shipping.rateName || 'Regular', price: parseFloat(shipping.ratePrice) }
                } else if (shipping.type === 'custom' && shipping.customPrice) {
                    input.shippingLine = { title: shipping.customName || 'Custom', price: parseFloat(shipping.customPrice) }
                }
            }

            const result = await createDraftOrder(input)
            const id = result.id.split('/').pop()
            navigate(`/draft-orders/${id}`)
        } catch (err: any) {
            setError(err.message || 'Failed to create draft order')
        } finally {
            setCreating(false)
        }
    }, [customerSelection, lineItems, navigate, discount, shipping, chargeTaxes, hasShippableItems, addressOverrides])

    return (
        <Page title="Create Draft Order">
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <BlockStack gap="400">
                        <Card>
                            <BlockStack gap="400">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Products</Text>
                                    <Button icon={PlusIcon} onClick={openProductPicker}>Add product</Button>
                                </InlineStack>

                                {lineItems.length === 0 ? (
                                    <BlockStack inlineAlign="center">
                                        <Text as="p" tone="subdued">No products added yet</Text>
                                    </BlockStack>
                                ) : (
                                    <BlockStack gap="0">
                                        {lineItems.map(li => {
                                            const lineTotal = parseFloat(li.price) * li.quantity
                                            return (
                                                <div key={li.variantId} style={{ padding: '12px 0', borderTop: '1px solid #e1e3e5' }}>
                                                    <InlineStack gap="300" blockAlign="center" align="space-between">
                                                        <InlineStack gap="300" blockAlign="center">
                                                            {li.imageUrl ? (
                                                                <Thumbnail source={li.imageUrl} alt={li.productTitle} size="small" />
                                                            ) : (
                                                                <div style={{ width: 40, height: 40, background: '#f1f1f1', borderRadius: 8 }} />
                                                            )}
                                                            <BlockStack gap="0">
                                                                <Text as="span" fontWeight="semibold">{li.productTitle}</Text>
                                                                {li.variantTitle && (
                                                                    <Text as="span" tone="subdued" variant="bodySm">{li.variantTitle}</Text>
                                                                )}
                                                            </BlockStack>
                                                        </InlineStack>
                                                        <InlineStack gap="300" blockAlign="center">
                                                            <Text as="span">{formatCurrency(li.price, STORE_CURRENCY)}</Text>
                                                            <div style={{ width: '70px' }}>
                                                                <TextField
                                                                    label="Qty"
                                                                    labelHidden
                                                                    type="number"
                                                                    value={String(li.quantity)}
                                                                    onChange={(v) => updateQuantity(li.variantId, parseInt(v) || 1)}
                                                                    autoComplete="off"
                                                                    min={1}
                                                                />
                                                            </div>
                                                            <Text as="span" fontWeight="semibold">{formatCurrency(lineTotal, STORE_CURRENCY)}</Text>
                                                            <Button
                                                                icon={XIcon}
                                                                variant="plain"
                                                                tone="critical"
                                                                onClick={() => removeLineItem(li.variantId)}
                                                                accessibilityLabel="Remove"
                                                            />
                                                        </InlineStack>
                                                    </InlineStack>
                                                </div>
                                            )
                                        })}
                                    </BlockStack>
                                )}
                            </BlockStack>
                        </Card>

                        {lineItems.length > 0 && (
                            <Card>
                                <BlockStack gap="300">
                                    <Text as="h2" variant="headingMd">Payment</Text>
                                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', alignItems: 'baseline', gap: '8px' }}>
                                        <Text as="span">Subtotal</Text>
                                        <Text as="span" tone="subdued">{itemCount} {itemCount === 1 ? 'item' : 'items'}</Text>
                                        <Text as="span" alignment="end">{formatCurrency(subtotal, STORE_CURRENCY)}</Text>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', alignItems: 'baseline', gap: '8px' }}>
                                        <div style={{ marginLeft: '0.5px' }}><Button variant="plain" onClick={() => setDiscountOpen(true)}>Add discount</Button></div>
                                        <Text as="span" tone="subdued">{discountAmount > 0 ? (discount.reason || discount.code || (discount.discountType === 'percentage' ? `${discount.discountValue}% off` : 'Custom discount')) : '—'}</Text>
                                        <Text as="span" alignment="end">{discountAmount > 0 ? `-${formatCurrency(discountAmount, STORE_CURRENCY)}` : formatCurrency(0, STORE_CURRENCY)}</Text>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', alignItems: 'baseline', gap: '8px' }}>
                                        <div style={{ marginLeft: '0.5px' }}><Button variant="plain" onClick={() => setShippingOpen(true)}>Add shipping or delivery</Button></div>
                                        <Text as="span" tone="subdued">{shippingAmount > 0 ? (shipping.type === 'rate' ? shipping.rateName : shipping.customName) : '—'}</Text>
                                        <Text as="span" alignment="end">{formatCurrency(shippingAmount, STORE_CURRENCY)}</Text>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', alignItems: 'baseline', gap: '8px' }}>
                                        <TaxPopover chargeTaxes={chargeTaxes} onSave={setChargeTaxes} />
                                        <Text as="span" tone="subdued">{chargeTaxes ? 'Not calculated' : 'Not collected'}</Text>
                                        <Text as="span" alignment="end">{' '}</Text>
                                    </div>
                                    <Divider />
                                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', alignItems: 'baseline', gap: '8px' }}>
                                        <Text as="span" fontWeight="bold">Total</Text>
                                        <Text as="span">{' '}</Text>
                                        <Text as="span" fontWeight="bold" alignment="end">{formatCurrency(total, STORE_CURRENCY)}</Text>
                                    </div>
                                </BlockStack>

                                <DiscountModal
                                    open={discountOpen}
                                    discount={discount}
                                    onSave={setDiscount}
                                    onClose={() => setDiscountOpen(false)}
                                />
                                <ShippingModal
                                    open={shippingOpen}
                                    shipping={shipping}
                                    hasShippableItems={hasShippableItems}
                                    onSave={setShipping}
                                    onClose={() => setShippingOpen(false)}
                                />
                            </Card>
                        )}
                    </BlockStack>
                </Layout.Section>

                <Layout.Section variant="oneThird">
                    <BlockStack gap="400">
                        <Card>
                            <BlockStack gap="300">
                                <CustomerPicker
                                    selection={customerSelection}
                                    onSelect={setCustomerSelection}
                                    onRemove={handleCustomerRemove}
                                    onAddressChange={setAddressOverrides}
                                />
                            </BlockStack>
                        </Card>

                        <Button
                            variant="primary"
                            size="large"
                            fullWidth
                            onClick={handleCreate}
                            loading={creating}
                            disabled={!canCreate}
                        >
                            Create Draft Order
                        </Button>
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    )
}
